import { getApp } from '@/apps/registry';
import type { AppArgs } from '@/wm/types';

export interface DeepLink {
  appId: string;
  args: AppArgs;
}

/**
 * Deep links use query (or hash) parameters rather than paths, so the site
 * stays a plain static bundle with no rewrite rules:
 *
 *   /?open=ie&url=https://blog.teon.io
 *   /?open=notepad&file=C:\Documents and Settings\Teon\Desktop\About me.txt
 *   /?open=explorer&path=C:\
 *   /?open=display
 *   #open=ie&url=…            (hash alias, handy for hand-written links)
 */
export function parseDeepLink(loc: Location | URL = window.location): DeepLink | null {
  const search = new URLSearchParams(loc.search);
  const hash = new URLSearchParams(loc.hash.replace(/^#\/?/, ''));
  const params = search.has('open') ? search : hash.has('open') ? hash : null;
  if (!params) return null;

  const appId = params.get('open');
  if (!appId || !getApp(appId)) return null;

  const args: AppArgs = {};
  params.forEach((value, key) => {
    if (key !== 'open') args[key] = value;
  });

  return { appId, args };
}

export function buildDeepLink(appId: string, args: AppArgs = {}): string {
  const params = new URLSearchParams({ open: appId });
  for (const [k, v] of Object.entries(args)) {
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      params.set(k, String(v));
    }
  }
  return `${window.location.pathname}?${params.toString()}`;
}

/**
 * `?noboot=1` skips the boot animation, `?desktop=1` forces the desktop shell on a phone,
 * and `?ipod=1` forces the iPod on a desktop — the last of those because the device is
 * otherwise reachable only by dragging a browser window under 768 pixels.
 */
export function hasFlag(flag: string): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get(flag) === '1';
}
